import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SearchService } from '@services/search.service';
import { DatarefSearchResult, Facet, FacetFilter, FieldFilter, SearchEvent, SearchPayload } from '@shared/models/search.model';
import { Sort } from '@angular/material/sort';
import { PAGE_SIZE } from '@shared/constants/page.constant';
import { forkJoin, Subject } from 'rxjs';
import { RefDataService } from '@services/ref-data.service';
import { Brand, Domain, Language, Market } from '@shared/models/dataref.model';
import { takeUntil, tap } from 'rxjs/operators';
import { PartnersSearchResultComponent } from './partners-search-result/partners-search-result.component';
import { PartnersSearchFacetsComponent } from './partners-search-facets/partners-search-facets.component';
import { FacetUtils } from '@shared/utils/facet-utils';
import { TablePaginatorComponent } from '@shared/components/data-table-manager/table-paginator/table-paginator.component';

@Component({
  selector: 'app-partners-search',
  templateUrl: './partners-search.component.html',
  styleUrls: ['./partners-search.component.scss'],
  standalone: false,
})
export class PartnersSearchComponent implements OnInit, OnDestroy {
  @ViewChild(PartnersSearchResultComponent) resultComponent?: PartnersSearchResultComponent;
  @ViewChild(PartnersSearchFacetsComponent) facetsComponent?: PartnersSearchFacetsComponent;

  brands: Brand[] = [];
  domains: Subject<Domain[]> = new Subject<Domain[]>();
  markets: Market[] = [];
  languages: Language[] = [];
  searchResult: Subject<DatarefSearchResult> = new Subject<DatarefSearchResult>();
  facets: Subject<Facet[]> = new Subject<Facet[]>();
  sort?: Sort;
  q?: string;
  fieldFilters: FieldFilter[] = [];
  facetFilters: FacetFilter[] = [];
  pageIndex = 0;
  pageSize = PAGE_SIZE;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private searchService: SearchService,
    private refDataService: RefDataService,
  ) {}

  ngOnDestroy(): void {
    this.domains.complete();
    this.facets.complete();
    this.searchResult.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    forkJoin(['domains', 'markets', 'languages'].map((key: string) => this.refDataService.getByKey(key, true)))
      .pipe(
        tap((response) => {
          this.domains.next(this.refDataService.translateReference(this.refDataService.toArray(response[0])));
          this.markets = this.refDataService.translateReference(this.refDataService.toArray(response[1]));
          this.languages = this.refDataService.translateReference(this.refDataService.toArray(response[2]));
          this.search();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  handleSearchQuery(event: SearchEvent): void {
    this.q = event.q;
    this.fieldFilters = event.fieldFilters;
    this.facetFilters = event.facetFilters;
    this.pageIndex = 0;
    this.search();
  }

  handleSearchPage(paginator: TablePaginatorComponent): void {
    this.pageIndex = paginator.pageIndex;
    this.pageSize = paginator.pageSize;
    this.search();
  }

  handleSearchSorted(sort: Sort): void {
    this.sort = sort;
    this.search();
  }

  handleSearchFacets(facetFilter: FacetFilter): void {
    this.facetFilters = FacetUtils.handleSearchFacets(this.facetFilters, facetFilter);
    this.pageIndex = 0;
    this.search();
  }

  search(): void {
    this.searchService
      .searchDataReferences(
        'partners',
        this.buildSearchPayload(this.resultComponent ? this.resultComponent.sort : undefined),
        false,
        this.q,
        this.pageSize,
        this.pageIndex,
      )
      .pipe(
        tap((response) => {
          this.searchResult.next(response);
          if (response.facets) {
            const index = response.facets.findIndex((f: Facet) => f.facet === 'type');
            if (index !== -1) {
              // move Type on top
              response.facets = [response.facets[index], ...response.facets];
              response.facets.splice(index + 1, 1);
            }
            this.facets.next(response.facets);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private buildSearchPayload(sort?: Sort): SearchPayload {
    const payload: SearchPayload = {
      fieldList: ['key', 'type', 'medias', 'name', 'targetDomain'],
      facetList: [{ name: 'targetDomain' }, { name: 'name.lang' }, { name: 'type' }],
      sort: {
        name: sort?.active ?? 'name',
        direction: sort?.direction ?? 'ASC',
      },
    };
    if (this.fieldFilters.length > 0) {
      payload.fieldFilters = this.fieldFilters;
    }
    if (this.facetFilters.length > 0) {
      payload.facetFilters = this.facetFilters;
    }
    return payload;
  }
}
